import { z } from "zod"

import { tenantIdSchema } from "./identifiers.js"
import { platformCommandEnvelopeV1Schema } from "./platform-command-v1.js"

const timestampSchema = z.iso.datetime({ offset: true })
const siteNameSchema = z.string().regex(/^[a-z0-9][a-z0-9.-]{2,127}$/)
const hostnameSchema = z.string().regex(
  /^(?=.{4,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/,
)

export const tenantRecoveryOperationV1Schema = z.enum([
  "backup",
  "restore",
  "export",
  "delete",
])

export const tenantRecoveryCheckpointV1Schema = z.enum([
  "target_verified",
  "backup_verified",
  "isolated_restore_verified",
  "registry_verified",
  "providers_reconciled",
  "export_verified",
  "retention_verified",
  "route_withdrawn",
  "providers_disconnected",
  "encryption_keys_destroyed",
  "failure_cleanup_verified",
  "completed",
])

export const tenantRecoveryFailureCodeV1Schema = z.enum([
  "TARGET_IDENTITY_MISMATCH",
  "APPROVAL_INVALID",
  "BACKUP_NOT_FRESH",
  "BACKUP_NOT_ENCRYPTED",
  "RESTORE_NOT_ISOLATED",
  "RESTORE_HEALTH_FAILED",
  "REGISTRY_MISMATCH",
  "PROVIDER_RECONCILIATION_FAILED",
  "EXPORT_FAILED",
  "RETENTION_NOT_ELIGIBLE",
  "LEGAL_HOLD_ACTIVE",
  "ROUTE_WITHDRAWAL_FAILED",
  "PROVIDER_DISCONNECT_FAILED",
  "KEY_DESTRUCTION_FAILED",
  "FAILURE_CLEANUP_FAILED",
  "OPERATION_FAILED",
])

export const tenantRecoveryRequestV1Schema = z.object({
  contractVersion: z.literal(1),
  operationId: z.uuid(),
  operation: tenantRecoveryOperationV1Schema,
  targetTenantId: tenantIdSchema,
  siteName: siteNameSchema,
  hostname: hostnameSchema,
  sourceBackupId: z.uuid().nullable(),
  reasonCode: z.string().regex(/^[a-z][a-z0-9_.-]{2,63}$/),
}).strict().superRefine((request, context) => {
  if (
    (request.operation === "restore" && request.sourceBackupId === null)
    || (request.operation !== "restore" && request.sourceBackupId !== null)
  ) {
    context.addIssue({
      code: "custom",
      path: ["sourceBackupId"],
      message: "Only restore requires one exact source backup.",
    })
  }
})

export const executeTenantRecoveryOperationV1Schema = z.object({
  envelope: platformCommandEnvelopeV1Schema.safeExtend({
    commandType: z.literal("ExecuteTenantRecoveryOperation"),
  }),
  request: tenantRecoveryRequestV1Schema,
}).strict().superRefine((command, context) => {
  const { envelope, request } = command
  if (
    envelope.commandVersion !== 1
    || envelope.idempotencyNamespace !== "tenant.recovery.execute"
    || envelope.target.scope !== "tenant"
    || envelope.target.targetTenantId !== request.targetTenantId
    || envelope.target.targetType !== "tenant_recovery_operation"
    || envelope.target.targetId !== request.operationId
    || (
      ["restore", "delete"].includes(request.operation)
      && envelope.requiredApprovalIds.length < 1
    )
  ) {
    context.addIssue({
      code: "custom",
      path: ["envelope"],
      message: "Recovery command must bind one exact Tenant operation and approval.",
    })
  }
})

export const tenantRecoveryBackupEvidenceV1Schema = z.object({
  backupId: z.uuid(),
  targetTenantId: tenantIdSchema,
  createdAt: timestampSchema,
  encrypted: z.literal(true),
  databaseAndFilesSeparated: z.literal(true),
  ageSeconds: z.number().int().min(0).max(31_536_000),
  withinRpo: z.boolean(),
}).strict()

export const tenantRecoveryRestoreEvidenceV1Schema = z.object({
  restoreId: z.uuid(),
  targetTenantId: tenantIdSchema,
  isolated: z.literal(true),
  registryMatches: z.literal(true),
  healthVerified: z.literal(true),
}).strict()

export const tenantRecoveryProviderEvidenceV1Schema = z.object({
  stripe: z.enum(["not_required", "reconciled"]),
  worklio: z.enum(["not_required", "reconciled"]),
}).strict()

export const tenantRecoveryExportEvidenceV1Schema = z.object({
  exportReferenceId: z.uuid(),
  targetTenantId: tenantIdSchema,
  encrypted: z.literal(true),
  completedAt: timestampSchema,
}).strict()

export const tenantRecoveryDeletionEvidenceV1Schema = z.object({
  deletionEvidenceReferenceId: z.uuid(),
  routeWithdrawn: z.literal(true),
  providersDisconnected: z.literal(true),
  encryptionKeysDestroyed: z.literal(true),
  immutableAuditPreserved: z.literal(true),
  completedAt: timestampSchema,
}).strict()

export const tenantRecoveryCleanupEvidenceV1Schema = z.object({
  attempted: z.literal(true),
  isolatedRestoreRemoved: z.boolean(),
  temporaryExportRemoved: z.boolean(),
  residualRoutePublished: z.literal(false),
  completedAt: timestampSchema,
}).strict()

export const tenantRecoverySnapshotV1Schema = z.object({
  responseVersion: z.literal(1),
  operationId: z.uuid(),
  operation: tenantRecoveryOperationV1Schema,
  targetTenantId: tenantIdSchema,
  siteName: siteNameSchema,
  hostname: hostnameSchema,
  state: z.enum(["running", "succeeded", "failed"]),
  checkpoint: tenantRecoveryCheckpointV1Schema,
  backup: tenantRecoveryBackupEvidenceV1Schema.nullable(),
  restore: tenantRecoveryRestoreEvidenceV1Schema.nullable(),
  providers: tenantRecoveryProviderEvidenceV1Schema,
  export: tenantRecoveryExportEvidenceV1Schema.nullable(),
  deletion: tenantRecoveryDeletionEvidenceV1Schema.nullable(),
  cleanup: tenantRecoveryCleanupEvidenceV1Schema.nullable(),
  failureCode: tenantRecoveryFailureCodeV1Schema.nullable(),
  elapsedSeconds: z.number().int().min(0).max(604_800),
  withinRto: z.boolean(),
  revision: z.number().int().positive(),
  startedAt: timestampSchema,
  completedAt: timestampSchema.nullable(),
  updatedAt: timestampSchema,
  valueFree: z.literal(true),
}).strict().superRefine((snapshot, context) => {
  if (
    (snapshot.state === "running" && (
      snapshot.completedAt !== null
      || snapshot.failureCode !== null
      || snapshot.cleanup !== null
    ))
    || (snapshot.state === "succeeded" && (
      snapshot.completedAt === null
      || snapshot.failureCode !== null
      || snapshot.checkpoint !== "completed"
    ))
    || (snapshot.state === "failed" && (
      snapshot.completedAt === null
      || snapshot.failureCode === null
      || snapshot.cleanup === null
      || snapshot.checkpoint !== "failure_cleanup_verified"
    ))
  ) {
    context.addIssue({
      code: "custom",
      path: ["state"],
      message: "Recovery state, completion, failure, and cleanup must agree.",
    })
  }
  if (
    snapshot.operation === "restore"
    && snapshot.state === "succeeded"
    && (snapshot.restore === null
      || snapshot.providers.stripe !== "reconciled"
      || snapshot.providers.worklio !== "reconciled")
  ) {
    context.addIssue({
      code: "custom",
      path: ["restore"],
      message: "Successful restore requires isolation and provider reconciliation.",
    })
  }
  if (
    snapshot.operation === "delete"
    && snapshot.state === "succeeded"
    && (snapshot.backup === null
      || snapshot.export === null
      || snapshot.deletion === null)
  ) {
    context.addIssue({
      code: "custom",
      path: ["deletion"],
      message: "Successful deletion requires backup, export, and deletion evidence.",
    })
  }
})

export const tenantRecoveryResponseV1Schema = z.object({
  responseVersion: z.literal(1),
  replayed: z.boolean(),
  snapshot: tenantRecoverySnapshotV1Schema,
}).strict()

export type TenantRecoveryOperationV1 = z.infer<
  typeof tenantRecoveryOperationV1Schema
>
export type TenantRecoveryCheckpointV1 = z.infer<
  typeof tenantRecoveryCheckpointV1Schema
>
export type TenantRecoveryFailureCodeV1 = z.infer<
  typeof tenantRecoveryFailureCodeV1Schema
>
export type TenantRecoveryBackupEvidenceV1 = z.infer<
  typeof tenantRecoveryBackupEvidenceV1Schema
>
export type TenantRecoveryRestoreEvidenceV1 = z.infer<
  typeof tenantRecoveryRestoreEvidenceV1Schema
>
export type TenantRecoveryExportEvidenceV1 = z.infer<
  typeof tenantRecoveryExportEvidenceV1Schema
>
export type TenantRecoveryDeletionEvidenceV1 = z.infer<
  typeof tenantRecoveryDeletionEvidenceV1Schema
>
export type TenantRecoveryCleanupEvidenceV1 = z.infer<
  typeof tenantRecoveryCleanupEvidenceV1Schema
>
export type TenantRecoverySnapshotV1 = z.infer<
  typeof tenantRecoverySnapshotV1Schema
>
export type ExecuteTenantRecoveryOperationV1 = z.infer<
  typeof executeTenantRecoveryOperationV1Schema
>
export type TenantRecoveryResponseV1 = z.infer<
  typeof tenantRecoveryResponseV1Schema
>
